import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  TextField,
  FormControlLabel,
  Checkbox,
} from "@mui/material";


const pickNonEmpty = (...vals) => {
  for (const v of vals) {
    if (v === 0) return v;
    if (v !== undefined && v !== null) {
      const s = String(v).trim();
      if (s && s.toLowerCase() !== "null" && s.toLowerCase() !== "undefined") return v;
    }
  }
  return "";
};

const toDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : d.toLocaleDateString();
};

const pad2 = (n) => String(n).padStart(2, "0");
// YYYY-MM-DD hh:mm AM
const fmtYmdDateTime = (val) => {
  if (!val) return "";
  const d = new Date(val);
  if (isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  let h = d.getHours();
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  const hh = pad2(h);
  const min = pad2(d.getMinutes());
  return `${yyyy}-${mm}-${dd} ${hh}:${min} ${ampm}`;
};

// MM/DD/YYYY
const fmtMMDDYYYY = (val) => {
  if (!val) return "N/A";
  const d = new Date(val);
  if (isNaN(d.getTime())) return "N/A";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

// Parse to array if already an array or JSON-stringified
const ensureArray = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === "string") {
    const t = v.trim();
    if (!t) return [];
    try {
      const parsed = JSON.parse(t);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const ARMHSITPPreview = ({ data }) => {
  const [itp, setItp] = useState(null); // ITP payload (getIndividualtreatment)
  const [form, setForm] = useState({}); // Base form payload (GetPatientFormsbyId)
  const [signatures, setSignatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const { patientFormId } = data || {};

  useEffect(() => {
    if (!patientFormId) return;
    let active = true;

    const fetchSignatures = async (noteData, baseForm, token) => {
      // Prefer ITP, then base form fields
      const urls = {
        client: pickNonEmpty(
          noteData?.clientSignaturePath,
          noteData?.clientSignatureURL,
          noteData?.clientsURL,
          baseForm?.clientSignatureURL,
          baseForm?.patientSignatureURL,
          baseForm?.clientsURL
        ),
        guardian: pickNonEmpty(
          noteData?.guardianSignatureURL,
          baseForm?.guardianURL,
          baseForm?.guardianSignatureURL
        ),
      };

      const loaded = {};
      for (const [key, path] of Object.entries(urls)) {
        if (!path) continue;

        // If already an absolute URL or data/blob, use directly
        if (/^(https?:|data:|blob:)/i.test(path)) {
          loaded[key] = path;
          continue;
        }

        try {
          const res = await fetch(
            `${API_BASE_URL}/PatientDocument/ViewPatientDocument?path=${encodeURIComponent(path)}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                Accept: "text/plain, */*",
              },
            }
          );
          if (!res.ok) continue;

          const ct = (res.headers.get("content-type") || "").toLowerCase();

          if (ct.includes("text")) {
            // API returns a direct URL as text
            const link = (await res.text()).trim();
            if (link) loaded[key] = link;
          } else {
            // Binary image bytes
            const blob = await res.blob();
            if (blob.size > 0) loaded[key] = URL.createObjectURL(blob);
          }
        } catch (err) {
          console.warn("Failed to load signature:", key, err);
        }
      }

      if (active) setSignatures(loaded);
    };

    const fetchAll = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('neuro_patientportal_auth_token')
        if (!token) throw new Error("Authentication token missing.");

        const [formRes, itpRes] = await Promise.all([
          fetch(`${API_BASE_URL}/Forms/GetPatientFormsbyId?Id=${patientFormId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(
            `${API_BASE_URL}/Rehabilitative/getIndividualtreatment?patientformID=${patientFormId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          ),
        ]);

        if (itpRes.status === 204) {
          setItp({});
          setForm({});
          setLoading(false);
          return;
        }
        if (!formRes.ok || !itpRes.ok)
          throw new Error("Failed to load ARMHS ITP form.");

        const [formDataRaw, itpDataRaw] = await Promise.all([
          formRes.json(),
          itpRes.json(),
        ]);

        const formData = Array.isArray(formDataRaw) ? (formDataRaw[0] || {}) : (formDataRaw || {});
        const itpData = Array.isArray(itpDataRaw) ? (itpDataRaw[0] || {}) : (itpDataRaw || {});

        setForm(formData);
        setItp(itpData);
        await fetchSignatures(itpData, formData, token);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
    return () => {
      active = false;
    };
  }, [API_BASE_URL, patientFormId]);

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", height: "60vh", alignItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  if (error)
    return (
      <Typography color="error" align="center" sx={{ p: 3 }}>
        {error}
      </Typography>
    );
  if (!itp)
    return (
      <Typography color="textSecondary" align="center" sx={{ p: 3 }}>
        No data available.
      </Typography>
    );

  // Patient strip (fallbacks to base form)
  const fileNo = pickNonEmpty(itp.fileno, form.fileno, form.accountNo, form.fileNo, "—") || "—";
  const patientName = pickNonEmpty(itp.patientName, form.patientName, "—") || "—";
  const dobDisplay = toDate(pickNonEmpty(itp.dateOfBirth, form.dob, form.dateOfBirth));

  // Dates
  const dosDisplay = toDate(pickNonEmpty(itp.assessmentDate, form.assessmentDate));
  const dateFA = toDate(pickNonEmpty(itp.dateFA, itp.recentFA));
  const dateDA = toDate(pickNonEmpty(itp.dateDA, itp.recentDA));
  const planDateDisplay = fmtMMDDYYYY(pickNonEmpty(itp.planDate, itp.Plandate));

  // Title from main form
  const titleDisplay = pickNonEmpty(form.Formtitle, form.customFormTitle, "—");

  // Text areas
  const functionalBarriers = pickNonEmpty(itp.functionalBarriers, "");
  const strengths = pickNonEmpty(itp.strengths, "");
  const culturalNeeds = pickNonEmpty(itp.culturalNeeds, "");
  const clientRecovery = pickNonEmpty(itp.clientRecovery, itp.recoveryVision, "");
  const summary = pickNonEmpty(itp.summary, itp.progressSummary, "");
  const clientHistory = pickNonEmpty(itp.clientHistory, "");

  // Arrays
  const rowsindividuals = ensureArray(
    pickNonEmpty(itp.individualslist, itp.rowsindividuals, itp.individualsInvolvedList, itp.individualsinvolvedlist)
  );
  const rowsRehabGoal = ensureArray(
    pickNonEmpty(itp.rehabGoallist, itp.rowsRehabGoal, itp.rehabGoal, itp.rehabGoals)
  );
  const rowscoordinationServices = ensureArray(
    pickNonEmpty(itp.coordinationServiceslist, itp.rowscoordinationServices, itp.coordinationServicesList)
  );

  // Agreement radio
  const sitReading = pickNonEmpty(itp.agreetreatmentplan, itp.sitReading, "");

  // Document Status
  const printed = !!pickNonEmpty(itp.isPrinted, form.isPrinted, false);
  const inactive = !!pickNonEmpty(itp.inactive, form.inactive, false);
  const auditReady = !!pickNonEmpty(itp.isAuditReady, form.isAuditReady, false);
  const locked = !!pickNonEmpty(itp.isChecked, form.isChecked, form.formlock, false);

  // Signature messages
  const normalizeMsg = (s) => {
    const t = String(s || "").trim();
    if (!t) return "";
    return t.replace(/\s*\.*\s*$/, "."); // ensure single trailing period
  };
  const practitionerMsg = normalizeMsg(pickNonEmpty(itp.messagepractitioner, form.messagepractitioner));
  const directorMsg = normalizeMsg(pickNonEmpty(itp.messagetreatmentdirector, form.messagetreatmentdirector));
  const professionalMsg = normalizeMsg(
    pickNonEmpty(itp.massagename, itp.messageProfessionalSignature, itp.professional, form.message)
  );

  // Client/Guardian signed date in MM/DD/YYYY
  const clientSignedText = fmtMMDDYYYY(
    pickNonEmpty(itp.clientDate, form.patientSignatureDATE, form.clientSignatureDATE)
  );
  const guardianSignedText = fmtMMDDYYYY(
    pickNonEmpty(itp.legalGuardianDate, form.legalGuardianDate)
  );

  return (
    <Paper
      sx={{
        p: 4,
        bgcolor: "#fff",
        borderRadius: 2,
        boxShadow: "0 3px 8px rgba(0,0,0,0.08)",
      }}
    >
      {/* Sticky header */}
      <Box
        sx={{
          position: "sticky",
          top: "-16px",
          zIndex: (theme) => theme.zIndex.appBar,
          bgcolor: "#fff",
          p: 4,
          pb: 0,
          mb: 3,
        }}
      >
        <Typography variant="subtitle2" sx={{ position: "absolute", top: 16, left: 16 }}>
          <strong>DOS:</strong> {dosDisplay || "—"}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            position: "absolute",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            fontWeight: "bold",
          }}
        >
          ARMHS ITP
        </Typography>
        <Box sx={{ height: 28 }} />
        <Divider sx={{ mx: -4 }} />
      </Box>

      {/* Patient strip */}
      <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Grid item xs={4}>
          <Typography variant="body2">
            <strong>File #:</strong> {fileNo || "—"}
          </Typography>
        </Grid>
        <Grid item xs={4} textAlign="center">
          <Typography variant="body2">
            <strong>Client Name:</strong> {patientName || "—"}
          </Typography>
        </Grid>
        <Grid item xs={4} textAlign="right">
          <Typography variant="body2">
            <strong>DOB:</strong> {dobDisplay || "—"}
          </Typography>
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* BASIC INFO */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <TextField
            label="Title"
            value={titleDisplay || "—"}
            fullWidth={false}
            size="small"
            InputProps={{ readOnly: true }}
            sx={{
              "& .MuiInputBase-root": {
                height: 40,
                fontSize: "0.900rem",
                padding: "0 10px",
                width: "350px",
              },
              "& .MuiInputLabel-root": { fontSize: "0.875rem" },
              "& .MuiSvgIcon-root": { display: "none" },
            }}
          />
        </Grid>
        <Divider sx={{ my: 2, mx: 5 }} />
        <Grid item xs={12} md={4}>
          <TextField
            label="Assessment Date"
            value={dosDisplay || "—"}
            fullWidth={false}
            size="small"
            InputProps={{ readOnly: true }}
            sx={{
              "& .MuiInputBase-root": {
                height: 40,
                fontSize: "0.900rem",
                padding: "0 10px",
                width: "350px",
              },
              "& .MuiInputLabel-root": { fontSize: "0.875rem" },
              "& .MuiSvgIcon-root": { display: "none" },
            }}
          />
        </Grid>
      </Grid>

      {/* Document Status */}
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Document Status
      </Typography>
      <Grid container spacing={1} sx={{ mb: 3 }}>
        <Grid item>
          <FormControlLabel control={<Checkbox disabled checked={printed} />} label="Printed and Filed" />
        </Grid>
        <Grid item>
          <FormControlLabel control={<Checkbox disabled checked={!!inactive} />} label="Inactive" />
        </Grid>
        <Grid item>
          <FormControlLabel control={<Checkbox disabled checked={auditReady} />} label="Audit Ready" />
        </Grid>
        <Grid item>
          <FormControlLabel control={<Checkbox disabled checked={locked} />} label="Lock" />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* Individuals Involved in ITP Development */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid black" }}>
          <thead>
            <tr>
              <th style={{ width: "50%", padding: "8px", border: "1px solid black", fontWeight: 700, color: "black", textAlign: "left" }}>
                Individuals involved in the development of this ITP
              </th>
              <th style={{ width: "50%", padding: "8px", border: "1px solid black", fontWeight: 700, color: "black", textAlign: "left" }}>
                Client/Agency/Title/Family Members/Others (specify)
              </th>
            </tr>
          </thead>
          <tbody>
            {rowsindividuals.length > 0 ? (
              rowsindividuals.map((individual, index) => (
                <tr key={index}>
                  <td style={{ width: "50%", padding: "8px", border: "1px solid black", verticalAlign: "top", color: "black" }}>
                    {pickNonEmpty(individual?.developmentofthisITP, individual?.name, "N/A")}
                  </td>
                  <td style={{ width: "50%", padding: "8px", border: "1px solid black", verticalAlign: "top" }}>
                    {pickNonEmpty(individual?.familyMembers, individual?.details, "N/A")}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={2} style={{ padding: "8px", border: "1px solid black", textAlign: "center" }}>
                  <Typography>No individuals listed</Typography>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Grid>

      {/* Assessment Dates */}
      <Grid item xs={12} sx={{ mt: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ fontWeight: 700, textAlign: "left" }}>
          Date of most recent Diagnostic Assessment: {dateDA || "N/A"}
        </Typography>
        <Typography sx={{ fontWeight: 700, textAlign: "right" }}>
          Date of most recent Functional Assessment: {dateFA || "N/A"}
        </Typography>
      </Grid>

      {/* Functional Barriers */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Typography sx={{ textAlign: "left", textDecoration: "underline", fontWeight: 700 }}>
          Functional Barriers to be Addressed in this Plan:
        </Typography>
        <Typography>{functionalBarriers || "N/A"}</Typography>
      </Grid>

      {/* Strengths and Resources */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Typography sx={{ textAlign: "left", textDecoration: "underline", fontWeight: 700 }}>
          Strengths and Resources:
        </Typography>
        <Typography>{strengths || "N/A"}</Typography>
      </Grid>

      {/* Cultural Considerations */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Typography sx={{ textAlign: "left", textDecoration: "underline", fontWeight: 700 }}>
          Cultural Considerations and Needs:
        </Typography>
        <Typography>{culturalNeeds || "N/A"}</Typography>
      </Grid>

      {/* Client Recovery Vision */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Typography sx={{ textAlign: "left", textDecoration: "underline", fontWeight: 700 }}>
          Client Recovery Vision:
        </Typography>
        <Typography>{clientRecovery || "N/A"}</Typography>
      </Grid>

      {/* Summary of Progress */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Typography sx={{ textAlign: "left", textDecoration: "underline", fontWeight: 700 }}>
          Summary of Progress on Previous Goals and Objectives:
        </Typography>
        <Typography>{summary || "N/A"}</Typography>
      </Grid>

      {/* Rehab Goals */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid black" }}>
          <thead>
            <tr>
              <th style={{ color: "black", textAlign: "center", width: "60%", padding: "8px", border: "1px solid black", fontWeight: 700 }}>Rehab Goal</th>
              <th style={{ color: "black", textAlign: "center", width: "20%", padding: "8px", border: "1px solid black", fontWeight: 700 }}>*MN</th>
              <th style={{ color: "black", textAlign: "center", width: "20%", padding: "8px", border: "1px solid black", fontWeight: 700 }}>Modality</th>
            </tr>
          </thead>
          <tbody>
            {rowsRehabGoal.length > 0 ? (
              rowsRehabGoal.map((item, index) => (
                <tr key={index}>
                  <td style={{ width: "60%", padding: "8px", border: "1px solid black", verticalAlign: "top" }}>
                    {pickNonEmpty(item?.rehabStatus, item?.goal, "N/A")}
                  </td>
                  <td style={{ width: "20%", padding: "8px", border: "1px solid black", verticalAlign: "top" }}>
                    {pickNonEmpty(item?.mN, item?.mn, "N/A")}
                  </td>
                  <td style={{ width: "20%", padding: "8px", border: "1px solid black", verticalAlign: "top" }}>
                    {pickNonEmpty(item?.modality, "N/A")}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3} style={{ padding: "8px", border: "1px solid black", textAlign: "center" }}>
                  <Typography>No rehab goals available</Typography>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Objectives Table */}
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            border: "1px solid black",
            tableLayout: "fixed",
            marginTop: "10px",
          }}
        >
          <thead>
            <tr>
              <th style={{ color: "black", textAlign: "center", width: "60%", padding: "8px", border: "1px solid black", fontWeight: 700 }}>
                Objective(s)
              </th>
              <th style={{ color: "black", textAlign: "center", width: "20%", padding: "8px", border: "1px solid black", fontWeight: 700 }}>
                Timeframe
              </th>
              <th style={{ color: "black", textAlign: "center", width: "20%", padding: "8px", border: "1px solid black", fontWeight: 700 }}>
                Frequency
              </th>
            </tr>
          </thead>
          <tbody>
            {rowsRehabGoal.length > 0 ? (
              rowsRehabGoal.flatMap((item, index) =>
                ensureArray(item?.rehabobjective).map((obj, objIndex) => (
                  <tr key={`${index}-${objIndex}`}>
                    <td style={{ width: "60%", padding: "8px", border: "1px solid black", verticalAlign: "top", wordWrap: "break-word", whiteSpace: "normal" }}>
                      {pickNonEmpty(obj?.objectives, obj?.objective, "N/A")}
                    </td>
                    <td style={{ width: "20%", padding: "8px", border: "1px solid black", verticalAlign: "top", wordWrap: "break-word", whiteSpace: "normal" }}>
                      {pickNonEmpty(obj?.timeframe, "N/A")}
                    </td>
                    <td style={{ width: "20%", padding: "8px", border: "1px solid black", verticalAlign: "top", wordWrap: "break-word", whiteSpace: "normal" }}>
                      {pickNonEmpty(obj?.frequency, "N/A")}
                    </td>
                  </tr>
                ))
              )
            ) : (
              <tr>
                <td colSpan={3} style={{ padding: "8px", border: "1px solid black", textAlign: "center" }}>
                  <Typography>No objectives available</Typography>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Interventions Table */}
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            border: "1px solid black",
            marginTop: "10px",
          }}
        >
          <thead>
            <tr>
              <th style={{ color: "black", textAlign: "center", width: "100%", padding: "8px", border: "1px solid black", fontWeight: 700 }}>
                Interventions/Treatment Strategies
              </th>
            </tr>
          </thead>
          <tbody>
            {rowsRehabGoal.length > 0 ? (
              rowsRehabGoal.flatMap((item, index) =>
                ensureArray(item?.interventionsStrategies).map((intervention, intIndex) => (
                  <tr key={`${index}-${intIndex}`}>
                    <td style={{ width: "100%", padding: "8px", border: "1px solid black", verticalAlign: "top" }}>
                      {pickNonEmpty(intervention?.interventions, "N/A")}
                    </td>
                  </tr>
                ))
              )
            ) : (
              <tr>
                <td style={{ padding: "8px", border: "1px solid black", textAlign: "center" }}>
                  <Typography>No interventions available</Typography>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Grid>

      {/* Client History for Engagement */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Typography sx={{ textAlign: "left", textDecoration: "underline", fontWeight: 700 }}>
          If a client has a history of not engaging in treatment, describe the treatment strategy to engage the client:
        </Typography>
        <Typography>{clientHistory || "N/A"}</Typography>
      </Grid>

      {/* Individual Received Copy */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Typography sx={{ textDecoration: "underline", fontWeight: 700 }}>
          This individual received a copy of this plan
        </Typography>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            <tr>
              <td style={{ padding: "8px", width: "10%", verticalAlign: "middle" }}>
                <input
                  type="checkbox"
                  checked={String(pickNonEmpty(itp.individualCopy, itp.individualCopyValue)).toLowerCase() === "yes"}
                  disabled
                  style={{ marginLeft: "10px", transform: "scale(1.0)", marginRight: "5px" }}
                />
                <Typography component="label" style={{ fontSize: "14px" }}>
                  Yes
                </Typography>
              </td>
              <td style={{ padding: "8px", width: "10%", verticalAlign: "middle" }}>
                <input
                  type="checkbox"
                  checked={String(pickNonEmpty(itp.individualCopy, itp.individualCopyValue)).toLowerCase() === "no"}
                  disabled
                  style={{ marginLeft: "10px", transform: "scale(1.0)", marginRight: "5px" }}
                />
                <Typography component="label" style={{ fontSize: "14px" }}>
                  No
                </Typography>
              </td>
              <td style={{ padding: "8px", width: "80%", verticalAlign: "middle" }}>
                <Typography>If no, specify reason: {pickNonEmpty(itp.reason, itp.noCopyReason, "N/A")}</Typography>
              </td>
            </tr>
          </tbody>
        </table>
      </Grid>

      {/* Client/Legal Guardian Agreement */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            <tr>
              <td style={{ padding: "8px", width: "50%", verticalAlign: "middle" }}>
                <Typography style={{ fontWeight: "bold", color: "black" }}>
                  Does client/legal guardian agree with this treatment plan?
                </Typography>
              </td>
              <td style={{ padding: "8px", width: "50%", verticalAlign: "middle" }}>
                <div>
                  <input
                    type="radio"
                    value=" Agree"
                    name="agreement"
                    checked={String(sitReading).trim() === "Agree" || String(sitReading) === " Agree"}
                    disabled
                    style={{ marginLeft: "30px", transform: "scale(1.0)", marginRight: "5px" }}
                  />
                  <label style={{ fontSize: "14px", fontWeight: "normal" }}>Agree</label>
                  <input
                    type="radio"
                    value="not Agree"
                    name="agreement"
                    checked={String(sitReading).trim().toLowerCase() === "not agree"}
                    disabled
                    style={{ marginLeft: "30px", transform: "scale(1.0)", marginRight: "5px" }}
                  />
                  <label style={{ fontSize: "14px", fontWeight: "normal" }}>Not Agree</label>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </Grid>

      {/* Signature section (rows with image and date on same line) */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Typography style={{ fontWeight: "bold", color: "black", fontSize: "16px" }}>
          Signature
        </Typography>

        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8 }}>
          <tbody>
            {/* Client row */}
            <tr>
              <td style={{ width: "25%", verticalAlign: "middle", padding: "8px" }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>Client</Typography>
              </td>
              <td style={{ width: "50%", padding: "8px", verticalAlign: "middle", textAlign: "center" }}>
                {signatures.client ? (
                  <div
                    style={{
                      width: 200,
                      height: 80,
                      border: "2px solid #000",
                      background: "#f5f5f5",
                      borderRadius: 8,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                    }}
                  >
                    <img
                      src={signatures.client}
                      alt="Client Signature"
                      style={{ width: "100%", height: "100%", objectFit: "contain" }}
                    />
                  </div>
                ) : (
                  <Typography sx={{ fontWeight: 700 }}>__</Typography>
                )}
              </td>
              <td style={{ width: "25%", verticalAlign: "middle", padding: "8px", textAlign: "right", whiteSpace: "nowrap" }}>
                <Typography>{clientSignedText !== "N/A" ? `Signed on ${clientSignedText}` : ""}</Typography>
              </td>
            </tr>

            {/* Client's Legal Guardian row */}
            <tr>
              <td style={{ width: "25%", verticalAlign: "middle", padding: "8px" }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>Client's Legal Guardian</Typography>
              </td>
              <td style={{ width: "50%", padding: "8px", verticalAlign: "middle", textAlign: "center" }}>
                {signatures.guardian ? (
                  <div
                    style={{
                      width: 200,
                      height: 80,
                      border: "2px solid #000",
                      background: "#f5f5f5",
                      borderRadius: 8,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                    }}
                  >
                    <img
                      src={signatures.guardian}
                      alt="Guardian Signature"
                      style={{ width: "100%", height: "100%", objectFit: "contain" }}
                    />
                  </div>
                ) : (
                  <Typography sx={{ fontWeight: 700 }}>__</Typography>
                )}
              </td>
              <td style={{ width: "25%", verticalAlign: "middle", padding: "8px", textAlign: "right", whiteSpace: "nowrap" }}>
                <Typography>{guardianSignedText !== "N/A" ? `Signed on ${guardianSignedText}` : ""}</Typography>
              </td>
            </tr>

            {/* Mental Health Practitioner row */}
            <tr>
              <td style={{ width: "75%", verticalAlign: "top", padding: "8px" }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>Mental Health Practitioner</Typography>
              </td>
              <td colSpan={2} style={{ padding: "8px", verticalAlign: "top" }}>
                <Typography sx={{ fontWeight: "bold", color: "black" }}>
                  {practitionerMsg || "__"}
                </Typography>
              </td>
            </tr>

            {/* ARMHS Treatment Director row */}
            <tr>
              <td style={{ width: "25%", verticalAlign: "top", padding: "8px" }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>ARMHS Treatment Director</Typography>
              </td>
              <td colSpan={2} style={{ padding: "8px", verticalAlign: "top" }}>
                <Typography sx={{ fontWeight: "bold", color: "black" }}>
                  {directorMsg || "__"}
                </Typography>
              </td>
            </tr>

            {/* Mental Health Professional row */}
            <tr>
              <td style={{ width: "25%", verticalAlign: "top", padding: "8px" }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>Mental Health Professional</Typography>
              </td>
              <td colSpan={2} style={{ padding: "8px", verticalAlign: "top" }}>
                <Typography sx={{ fontWeight: "bold", color: "black" }}>
                  {professionalMsg || "__"}
                </Typography>
              </td>
            </tr>
          </tbody>
        </table>
      </Grid>

      {/* Plan Update */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Typography sx={{ textDecoration: "underline", fontWeight: 700 }}>
          Plan Update:
        </Typography>
        <Typography sx={{ mt: 1 }}>
          This plan must be updated at least every 180 days or more often when there is significant change in the client's situation or functioning,
          or in services or service methods to be used or at the request of the client or the client's legal guardian.
        </Typography>
        <Typography sx={{ mt: 1 }}>
          Proposed date of ITP meeting to update plan: {planDateDisplay}
        </Typography>
      </Grid>
    </Paper>
  );
};

export default ARMHSITPPreview;